import React, { useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  HStack,
  Grid,
  GridItem,
  useToast,
  Box,
  Text,
  Tag,
  TagLabel,
  TagCloseButton,
  InputGroup,
  InputLeftAddon,
  Divider,
  Flex,
  Heading,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiX } from "react-icons/fi";
import { jobRequisitionService } from "../jobRequisitionService";

const JobRequisitionForm = () => {
  const [formData, setFormData] = useState({
    jobTitle: "",
    department: "",
    location: "",
    employmentType: "Full-time",
    jobLevel: "Mid Level",
    experienceRequired: {
      min: 0,
      max: 0,
      preferred: "",
    },
    skills: [],
    qualifications: [],
    responsibilities: [],
    salaryRange: {
      min: 0,
      max: 0,
      currency: "INR",
    },
    budgetAllocated: 0,
    postingDate: "",
    closingDate: "",
    expectedJoiningDate: "",
    businessJustification: "",
    reportingManager: "",
    numberOfPositions: 1,
    priority: "Medium",
  });

  const [skillInput, setSkillInput] = useState("");
  const [qualificationInput, setQualificationInput] = useState("");
  const [responsibilityInput, setResponsibilityInput] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()],
      }));
      setSkillInput("");
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToRemove),
    }));
  };

  const addQualification = () => {
    if (
      qualificationInput.trim() &&
      !formData.qualifications.includes(qualificationInput.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        qualifications: [...prev.qualifications, qualificationInput.trim()],
      }));
      setQualificationInput("");
    }
  };

  const removeQualification = (qualificationToRemove) => {
    setFormData((prev) => ({
      ...prev,
      qualifications: prev.qualifications.filter(
        (qual) => qual !== qualificationToRemove,
      ),
    }));
  };

  const addResponsibility = () => {
    if (
      responsibilityInput.trim() &&
      !formData.responsibilities.includes(responsibilityInput.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        responsibilities: [
          ...prev.responsibilities,
          responsibilityInput.trim(),
        ],
      }));
      setResponsibilityInput("");
    }
  };

  const removeResponsibility = (responsibilityToRemove) => {
    setFormData((prev) => ({
      ...prev,
      responsibilities: prev.responsibilities.filter(
        (resp) => resp !== responsibilityToRemove,
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.jobTitle || !formData.department || !formData.location) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const response = await jobRequisitionService.createRequisition(formData);
      toast({
        title: "Requisition created",
        description: "Job requisition has been created successfully",
        status: "success",
        duration: 3000,
      });

      const createdRequisitionId =
        response?.data?.requisition?._id ||
        response?.requisition?._id ||
        response?._id;

      if (createdRequisitionId) {
        navigate(`/recruiting/requisitions/${createdRequisitionId}`);
      } else {
        navigate("/recruiting/requisitions");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error saving requisition",
        description: "Could not save the job requisition",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg={pageBg}
      px={{ base: 4, md: 6 }}
      py={{ base: 4, md: 6 }}
    >
      <Box
        maxW="6xl"
        mx="auto"
        bg={cardBg}
        border="1px solid"
        borderColor={border}
        borderRadius="xl"
        p={{ base: 4, md: 6 }}
      >
        <Flex
          mb={6}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={3}
        >
          <Box>
            <Heading size="md">Create Job Requisition</Heading>
            <Text mt={1} color="gray.500" fontSize="sm">
              Fill in the details below to create a new requisition.
            </Text>
          </Box>
          <Button
            variant="ghost"
            onClick={() => navigate("/recruiting/requisitions")}
          >
            Back to Requisitions
          </Button>
        </Flex>

        <Box as="form" onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Basic Job Information */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                Basic Job Information
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <FormControl isRequired>
                  <FormLabel>Job Title</FormLabel>
                  <Input
                    value={formData.jobTitle}
                    onChange={(e) =>
                      handleInputChange("jobTitle", e.target.value)
                    }
                    placeholder="e.g., Senior Software Engineer"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Department</FormLabel>
                  <Select
                    value={formData.department}
                    onChange={(e) =>
                      handleInputChange("department", e.target.value)
                    }
                  >
                    <option value="">Select Department</option>
                    <option value="IT">IT</option>
                    <option value="HR">HR</option>
                    <option value="Finance">Finance</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Sales">Sales</option>
                    <option value="Operations">Operations</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Design">Design</option>
                    <option value="Product">Product</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Location</FormLabel>
                  <Input
                    value={formData.location}
                    onChange={(e) =>
                      handleInputChange("location", e.target.value)
                    }
                    placeholder="e.g., Mumbai, India"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Reporting Manager</FormLabel>
                  <Input
                    value={formData.reportingManager}
                    onChange={(e) =>
                      handleInputChange("reportingManager", e.target.value)
                    }
                    placeholder="e.g., John Doe"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Employment Type</FormLabel>
                  <Select
                    value={formData.employmentType}
                    onChange={(e) =>
                      handleInputChange("employmentType", e.target.value)
                    }
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                    <option value="Freelance">Freelance</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Job Level</FormLabel>
                  <Select
                    value={formData.jobLevel}
                    onChange={(e) =>
                      handleInputChange("jobLevel", e.target.value)
                    }
                  >
                    <option value="Entry Level">Entry Level</option>
                    <option value="Mid Level">Mid Level</option>
                    <option value="Senior Level">Senior Level</option>
                    <option value="Executive">Executive</option>
                    <option value="Management">Management</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Priority</FormLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) =>
                      handleInputChange("priority", e.target.value)
                    }
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Number of Positions</FormLabel>
                  <NumberInput
                    value={formData.numberOfPositions}
                    onChange={(value) =>
                      handleInputChange("numberOfPositions", parseInt(value))
                    }
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Grid>
            </Box>

            <Divider />

            {/* Requirements */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                Requirements
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <FormControl>
                  <FormLabel>Experience Required (Years)</FormLabel>
                  <HStack spacing={2}>
                    <NumberInput
                      value={formData.experienceRequired.min}
                      onChange={(value) =>
                        handleInputChange(
                          "experienceRequired.min",
                          parseInt(value) || 0,
                        )
                      }
                      min={0}
                    >
                      <NumberInputField placeholder="Min" />
                    </NumberInput>
                    <Text>to</Text>
                    <NumberInput
                      value={formData.experienceRequired.max}
                      onChange={(value) =>
                        handleInputChange(
                          "experienceRequired.max",
                          parseInt(value) || 0,
                        )
                      }
                      min={0}
                    >
                      <NumberInputField placeholder="Max" />
                    </NumberInput>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel>Preferred Experience</FormLabel>
                  <Input
                    value={formData.experienceRequired.preferred}
                    onChange={(e) =>
                      handleInputChange(
                        "experienceRequired.preferred",
                        e.target.value,
                      )
                    }
                    placeholder="e.g., 3+ years in React development"
                  />
                </FormControl>
              </Grid>

              {/* Skills */}
              <FormControl mt={4}>
                <FormLabel>Skills</FormLabel>
                <HStack spacing={2}>
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    placeholder="Add a skill..."
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addSkill())
                    }
                  />
                  <Button onClick={addSkill} colorScheme="blue" size="md">
                    <FiPlus />
                  </Button>
                </HStack>
                <Box mt={2}>
                  <HStack spacing={2} wrap="wrap">
                    {formData.skills.map((skill, index) => (
                      <Tag
                        key={index}
                        size="md"
                        variant="solid"
                        colorScheme="blue"
                      >
                        <TagLabel>{skill}</TagLabel>
                        <TagCloseButton onClick={() => removeSkill(skill)} />
                      </Tag>
                    ))}
                  </HStack>
                </Box>
              </FormControl>

              {/* Qualifications */}
              <FormControl mt={4}>
                <FormLabel>Qualifications</FormLabel>
                <HStack spacing={2}>
                  <Input
                    value={qualificationInput}
                    onChange={(e) => setQualificationInput(e.target.value)}
                    placeholder="Add a qualification..."
                    onKeyPress={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), addQualification())
                    }
                  />
                  <Button
                    onClick={addQualification}
                    colorScheme="green"
                    size="md"
                  >
                    <FiPlus />
                  </Button>
                </HStack>
                <Box mt={2}>
                  <VStack spacing={1} align="start">
                    {formData.qualifications.map((qualification, index) => (
                      <HStack key={index} spacing={2}>
                        <Text>• {qualification}</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => removeQualification(qualification)}
                        >
                          <FiX />
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </FormControl>

              {/* Responsibilities */}
              <FormControl mt={4}>
                <FormLabel>Key Responsibilities</FormLabel>
                <HStack spacing={2}>
                  <Input
                    value={responsibilityInput}
                    onChange={(e) => setResponsibilityInput(e.target.value)}
                    placeholder="Add a responsibility..."
                    onKeyPress={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), addResponsibility())
                    }
                  />
                  <Button
                    onClick={addResponsibility}
                    colorScheme="purple"
                    size="md"
                  >
                    <FiPlus />
                  </Button>
                </HStack>
                <Box mt={2}>
                  <VStack spacing={1} align="start">
                    {formData.responsibilities.map((responsibility, index) => (
                      <HStack key={index} spacing={2}>
                        <Text>• {responsibility}</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => removeResponsibility(responsibility)}
                        >
                          <FiX />
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </FormControl>
            </Box>

            <Divider />

            {/* Compensation & Budget */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                Compensation & Budget
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <FormControl>
                  <FormLabel>Salary Range</FormLabel>
                  <HStack spacing={2}>
                    <InputGroup>
                      <InputLeftAddon>₹</InputLeftAddon>
                      <NumberInput
                        value={formData.salaryRange.min}
                        onChange={(value) =>
                          handleInputChange(
                            "salaryRange.min",
                            parseInt(value) || 0,
                          )
                        }
                        min={0}
                      >
                        <NumberInputField placeholder="Min" />
                      </NumberInput>
                    </InputGroup>
                    <Text>to</Text>
                    <InputGroup>
                      <InputLeftAddon>₹</InputLeftAddon>
                      <NumberInput
                        value={formData.salaryRange.max}
                        onChange={(value) =>
                          handleInputChange(
                            "salaryRange.max",
                            parseInt(value) || 0,
                          )
                        }
                        min={0}
                      >
                        <NumberInputField placeholder="Max" />
                      </NumberInput>
                    </InputGroup>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel>Budget Allocated</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>₹</InputLeftAddon>
                    <NumberInput
                      value={formData.budgetAllocated}
                      onChange={(value) =>
                        handleInputChange(
                          "budgetAllocated",
                          parseInt(value) || 0,
                        )
                      }
                      min={0}
                    >
                      <NumberInputField placeholder="Total budget" />
                    </NumberInput>
                  </InputGroup>
                </FormControl>
              </Grid>
            </Box>

            <Divider />

            {/* Timeline */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                Timeline
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={4}
              >
                <FormControl>
                  <FormLabel>Posting Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.postingDate}
                    onChange={(e) =>
                      handleInputChange("postingDate", e.target.value)
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Closing Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.closingDate}
                    onChange={(e) =>
                      handleInputChange("closingDate", e.target.value)
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Expected Joining Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.expectedJoiningDate}
                    onChange={(e) =>
                      handleInputChange("expectedJoiningDate", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
            </Box>

            <Divider />

            {/* Business Justification */}
            <Box>
              <FormControl>
                <FormLabel>Business Justification</FormLabel>
                <Textarea
                  value={formData.businessJustification}
                  onChange={(e) =>
                    handleInputChange("businessJustification", e.target.value)
                  }
                  placeholder="Explain why this position is needed..."
                  rows={4}
                />
              </FormControl>
            </Box>
          </VStack>

          <Flex justify="flex-end" gap={3} mt={8}>
            <Button
              variant="ghost"
              onClick={() => navigate("/recruiting/requisitions")}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={loading}
              loadingText="Creating..."
            >
              Create Requisition
            </Button>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default JobRequisitionForm;
